import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs/internal/Observable';
import { catchError, filter, map, take } from 'rxjs/operators';
import {
  SignupResponse,
  Canal,
  QuickRegisterRequest,
  QuickRegisterResponse,
  Term,
} from '@libs/entity-lib';
import { throwError } from 'rxjs';
import { Store } from '@ngrx/store';
import { getCanal } from '../../store/settings/settings.selectors';
import { commonEnvironment } from '@libs/shared/util-environments';

@Injectable()
export class CrudLessorLibService {
  private http = inject(HttpClient);
  private store = inject(Store);

  private getCanal$ = this.store.select(getCanal);
  private baseUrl: string;

  constructor() {
    this.getCanal$
      .pipe(
        filter((canal) => canal !== Canal.UNDEFINED),
        take(1)
      )
      .subscribe((canal) => {
        this.setBaseUrlByCanal(canal);
      });
  }

  setBaseUrlByCanal(canal: Canal) {
    this.baseUrl = commonEnvironment.apps[canal].baseUrl;
  }

  create(model: any): Observable<SignupResponse> {
    const url = `${this.baseUrl}/signup/athlete`;
    return this.http.post<SignupResponse>(url, model).pipe(
      map((response: any) => response),
      catchError((error) => this.handleError(error))
    );
  }

  quickCreate(model: QuickRegisterRequest): Observable<QuickRegisterResponse> {
    const url = `${this.baseUrl}/signup/athlete`;
    return this.http.post<QuickRegisterResponse>(url, model).pipe(
      map((response: any) => response),
      catchError((error) => this.handleError(error))
    );
  }

  verifyUserName(userName: string): Observable<boolean> {
    const url = this.baseUrl + `/signup/verify?username=${userName}&canal=${Canal.ATHLETE}`;
    return this.http.get<boolean>(url).pipe(
      map((response: any) => response),
      catchError((error) => this.handleError(error))
    );
  }

  verifyEmail(email: string): Observable<boolean> {
    const url = this.baseUrl + `/signup/verify?email=${email}&canal=${Canal.ATHLETE}`;
    return this.http.get<boolean>(url).pipe(
      map((response: any) => response),
      catchError((error) => this.handleError(error))
    );
  }

  /** Dados públicos do CE para o cadastro por convite (id + nome + logo). */
  fetchPublicSportCenter(
    username: string
  ): Observable<{ id: number; name: string; image?: string }> {
    const url = `${this.baseUrl}/public/sport-centers/${encodeURIComponent(username)}`;
    return this.http
      .get<{ info?: { id: number; nomeCentroEsportivo?: string; image?: string } }>(url)
      .pipe(
        map((response) => ({
          id: response?.info?.id as number,
          name: response?.info?.nomeCentroEsportivo ?? '',
          image: response?.info?.image,
        })),
        catchError((error) => this.handleError(error))
      );
  }

  fetchTerms(): Observable<Term[]> {
    const url = `${this.baseUrl}/public/terms`;
    return this.http.get<{ data: Term[] }>(url).pipe(
      map((response) => response.data),
      catchError((error) => this.handleError(error))
    );
  }

  fetchAllCities() {
    const url = `${this.baseUrl}/public/cities`;
    return this.http.get<any>(url).pipe(map((response: any) => response));
  }

  formatToCnpj(value: string): string {
    const regex = /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/;
    return value ? value.replace(regex, '$1.$2.$3/$4-$5') : value;
  }

  formatToCpf(value: string): string {
    const regex = /(\d{3})(\d{3})(\d{3})(\d{2})/;
    return value ? value.replace(regex, '$1.$2.$3-$4') : value;
  }

  formatCellphone(phoneNumber: string): string {
    const regex = /^(\d{2})[\s.-]?(\d{5})[\s.-]?(\d{4})$/;
    const match = phoneNumber.match(regex);

    if (!match) {
      return phoneNumber;
    }

    const formattedNumber = `(${match[1]})${match[2]}-${match[3]}`;

    return formattedNumber;
  }

  formatCEP(value: string): string {
    const regex = /^([0-9]{5})([0-9]{3})$/;
    return value ? value.replace(regex, '$1-$2') : value;
  }

  isValidCNPJ(value: string): boolean {
    const regex = /^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$/;
    return regex.test(value);
  }

  isValidCPF(value: string): boolean {
    const regex = /^\d{3}\.\d{3}\.\d{3}-\d{2}$/;
    return regex.test(value);
  }

  isValidCEP(value: string): boolean {
    const regex = /^[0-9]{5}-[0-9]{3}$/;
    return regex.test(value);
  }

  private handleError(error: HttpErrorResponse) {
    if (error.status === 0) {
      // A client-side or network error occurred. Handle it accordingly.
      console.error('An error occurred:', error.error);
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong.
      console.error(`Backend returned code ${error.status}, body was: `, error.error);
    }
    // Return an observable with a user-facing error message.
    return throwError(() => new Error(error.error?.message));
  }
}
